/** @file Hook: moves focus into an open dialog, traps Tab inside it and closes it on Escape. */
import { useLayoutEffect, type KeyboardEvent, type RefObject } from 'react';
import { focusableSelector } from './logic/focusableSelector';
import { isRendered } from './logic/isRendered';
import { stepIndex } from './logic/stepIndex';

/** The elements inside `root` Tab can reach: in the tab order (no tabindex="-1") and actually rendered. */
function tabbable(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter(isRendered);
}

/**
 * Focuses the first control of `.p2-dialog-body` (else the dialog) when `open` turns true; returns the keydown handler.
 * The handler stops every keydown (the dialog is modal, so no global shortcut may fire behind it), closes on Escape and traps Tab.
 */
export function useDialogFocus(ref: RefObject<HTMLElement | null>, open: boolean, onClose: () => void): (e: KeyboardEvent<HTMLElement>) => void {
  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!open || !dialog) return;
    (tabbable(dialog.querySelector('.p2-dialog-body') ?? dialog)[0] ?? dialog).focus();
  }, [ref, open]);
  return (e) => {
    e.stopPropagation();
    if (e.nativeEvent.isComposing) return;
    if (e.code === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.code === 'Tab') {
      const items = tabbable(e.currentTarget);
      const current = items.findIndex((item) => item === document.activeElement);
      e.preventDefault();
      (items[stepIndex(current, e.shiftKey ? -1 : 1, items.map(() => false), true)] ?? e.currentTarget).focus();
    }
  };
}
